/** **`localStorage`** key for optimistic organizer session rendering on the home route. */
export const ORGANIZER_SESSION_CLIENT_HINT_KEY =
  "quiz_organizer_session_client_hint_v1";

const HINT_VERSION = 1 as const;

/** Serialized hint written before **`auth/me`** resolves or before login navigation. */
export type OrganizerSessionClientHint =
  | { readonly v: typeof HINT_VERSION; readonly kind: "pending" }
  | {
      readonly v: typeof HINT_VERSION;
      readonly kind: "session";
      readonly user_id: string;
      readonly display_name?: string | null;
    };

function parseHint(raw: string | null): OrganizerSessionClientHint | null {
  if (raw === null || raw === "") {
    return null;
  }
  try {
    const data = JSON.parse(raw) as unknown;
    if (typeof data !== "object" || data === null) {
      return null;
    }
    const rec = data as Record<string, unknown>;
    if (rec.v !== HINT_VERSION) {
      return null;
    }
    if (rec.kind === "pending") {
      return { v: HINT_VERSION, kind: "pending" };
    }
    if (
      rec.kind === "session" &&
      typeof rec.user_id === "string" &&
      rec.user_id.length > 0
    ) {
      const dn = rec.display_name;
      return {
        v: HINT_VERSION,
        kind: "session",
        user_id: rec.user_id,
        display_name:
          typeof dn === "string" || dn === null || dn === undefined
            ? (dn as string | null | undefined)
            : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Reads the last remembered organizer session hint for optimistic chrome (**HttpOnly** cookie cannot be read in JS).
 *
 * @returns Parsed hint or **`null`** when absent or malformed.
 */
export function readOrganizerSessionClientHint(): OrganizerSessionClientHint | null {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null;
  }
  try {
    return parseHint(localStorage.getItem(ORGANIZER_SESSION_CLIENT_HINT_KEY));
  } catch {
    return null;
  }
}

/**
 * Marks that a login navigation (**OAuth** redirect or bootstrap **`POST`**) is about to occur so the home route can render signed-in chrome optimistically.
 */
export function writeOrganizerSessionPendingHint(): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(
      ORGANIZER_SESSION_CLIENT_HINT_KEY,
      JSON.stringify({ v: HINT_VERSION, kind: "pending" }),
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Persists **`auth/me`** payload so the next home render can avoid flashing the anonymous marketing shell.
 *
 * @param payload - Session identifiers from **`GET`** **`/api/v1/auth/me`** (**200** body).
 */
export function writeOrganizerSessionSnapshotHint(payload: {
  user_id: string;
  display_name?: string | null;
}): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(
      ORGANIZER_SESSION_CLIENT_HINT_KEY,
      JSON.stringify({
        v: HINT_VERSION,
        kind: "session",
        user_id: payload.user_id,
        display_name: payload.display_name,
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Drops any remembered hint after logout or when **`auth/me`** reports unauthorized. */
export function clearOrganizerSessionClientHint(): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(ORGANIZER_SESSION_CLIENT_HINT_KEY);
  } catch {
    /* ignore */
  }
}
